import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
const PageIdentityContext = createContext(null);
/**
 * What page this is, said in one place (WCAG 2.4.2, Page Titled).
 *
 * A page names itself with `usePageTitle`; the provider turns that into the
 * document title — "Practices · Athena" — and hands the same name to whatever
 * draws the page's heading, so the tab, the heading and a screen reader's
 * page announcement never disagree. A name rather than a route option, so a
 * page whose name depends on what it loaded can start generic ("Practice")
 * and become specific ("Reading Comprehension") once the data arrives.
 *
 * A page that goes away takes its name with it: the next page's name replaces
 * it, and a page that sets none leaves just the suffix rather than the last
 * page's name on a page it does not belong to.
 */
export function PageIdentityProvider({ suffix, children }) {
    const [entry, setEntry] = useState(null);
    // Stable, so the effects that call them run when a page's name changes and
    // not every time the provider re-renders.
    const claim = useCallback((owner, title) => setEntry((current) => current?.owner === owner && current.title === title ? current : { owner, title }), []);
    const release = useCallback((owner) => setEntry((current) => (current?.owner === owner ? null : current)), []);
    const title = entry?.title ?? null;
    const value = useMemo(() => ({ title, claim, release }), [title, claim, release]);
    useEffect(() => {
        document.title = entry ? `${entry.title} · ${suffix}` : suffix;
    }, [entry, suffix]);
    return _jsx(PageIdentityContext.Provider, { value: value, children: children });
}
/**
 * Name the current page, already localized. Call it once per page, with the
 * generic name until a resource's own name is loaded:
 * `usePageTitle(data?.practice?.title ?? t('practice.title'))`.
 */
export function usePageTitle(title) {
    const context = useContext(PageIdentityContext);
    const owner = useRef(Symbol('page')).current;
    const claim = context?.claim;
    const release = context?.release;
    useEffect(() => {
        claim?.(owner, title);
    }, [claim, owner, title]);
    useEffect(() => () => {
        release?.(owner);
    }, [release, owner]);
}
/** The current page's name, for the one element that shows it as the page's heading. */
export function useCurrentPageTitle() {
    return useContext(PageIdentityContext)?.title ?? null;
}
//# sourceMappingURL=page-identity.js.map